/**
 * Sidebar panel that lets the user browse orderable imagery for the
 * currently-selected feature, hide individual scenes (owners only), and
 * launch the order modal. Extracted from MPV's `renderOrderImagery` helper.
 * Mounts in the right-side TabbedSidebar when the user opens the
 * OrderImagery view.
 */

import * as I from 'immutable';
import React from 'react';

import OrderImagerySidebar, {OrderImageryState} from 'app/components/OrderImagerySidebar';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiImageryContract, ApiProject} from 'app/modules/Remote/Project';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {OrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import {CachedApiGetterOptions, StatusMaybe} from 'app/utils/hookUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {Mode} from 'app/utils/mapUtils/modes';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import {OrderImageryFlow} from '../hooks/useOrderImageryFlow';
import {MapStateDispatch} from '../MapStateProvider';
import {setIsImageHidden} from '../utils';

interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  mode: Mode;
  imageRefs: mapUtils.MapImageRefs;
  selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>;
  selectedProject: I.ImmutableOf<ApiProject>;
  orderImageryState: OrderImageryState;
  setOrderImageryState: React.Dispatch<React.SetStateAction<OrderImageryState>>;
  orderableScenesLoader: OrderableScenesLoader;
  getCurrentImageryContract: (
    projectId: string,
    opts?: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;
  mapStateDispatch: MapStateDispatch;
  featuresActions: FeaturesActions;
  openOrderImageryModal: OrderImageryFlow['openOrderImageryModal'];
  setPreviewScenes: OrderImageryFlow['setPreviewScenes'];
}

export default function OrderImageryPanel({
  organization,
  profile,
  mode,
  imageRefs,
  selectedFeatures,
  selectedProject,
  orderImageryState,
  setOrderImageryState,
  orderableScenesLoader,
  getCurrentImageryContract,
  mapStateDispatch,
  featuresActions,
  openOrderImageryModal,
  setPreviewScenes,
}: Props) {
  const setIsHidden = (
    feature: I.ImmutableOf<ApiFeature>,
    orderableScene: I.ImmutableOf<ApiOrderableScene>,
    isHidden: boolean
  ) => {
    setIsImageHidden(featuresActions.updateFeature, feature, orderableScene, isHidden);
  };

  return (
    <OrderImagerySidebar
      organization={organization}
      profile={profile}
      mode={mode}
      imageRefs={imageRefs}
      onChange={(imageRef) =>
        mapStateDispatch({
          type: 'SET_IMAGE_REFS',
          imageRefs: [imageRef],
        })
      }
      feature={selectedFeatures.first()}
      setIsImageHidden={isOwnerOrAdmin(profile.get('role')) ? setIsHidden : null}
      openOrderImageryModal={openOrderImageryModal}
      onPreviewScenesChange={setPreviewScenes}
      orderImageryState={orderImageryState}
      setOrderImageryState={setOrderImageryState}
      orderableScenesLoader={orderableScenesLoader}
      getCurrentImageryContract={(opts) =>
        getCurrentImageryContract(selectedProject.get('id'), opts)
      }
    />
  );
}
